// ** React Imports
import { ReactNode, useEffect, useState } from 'react';
import 'material-icons/iconfont/material-icons.css';

// ** MUI Imports
import { Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

// ** Layout Imports
// !Do not remove this Layout import
import Layout from 'src/materio/@core/layouts/Layout';

// ** Navigation Imports
import VerticalNavItems from 'src/materio/navigation/vertical';
import HorizontalNavItems from 'src/materio/navigation/horizontal';

// ** Component Import
// Uncomment the below line (according to the layout type) when using server-side menu
// import ServerSideVerticalNavItems from './components/vertical/ServerSideNavItems'
// import ServerSideHorizontalNavItems from './components/horizontal/ServerSideNavItems'

import VerticalAppBarContent from './components/vertical/AppBarContent';
import HorizontalAppBarContent from './components/horizontal/AppBarContent';

// ** Hook Import
import { useSettings } from 'src/materio/@core/hooks/useSettings';

import { client } from 'src/utils/axios-client';
import { getLeadEndpoint, leadEndpoints, menuEndpoints } from 'src/configs/endpoints';
import HomeOutline from 'mdi-material-ui/HomeOutline';
import { Menu } from 'src/types/menu';
import ApplicationStore from '../../stores/ApplicationStore';
import { Icon, Snackbar } from '@mui/material';
import {
    ActivityTemplateSelectionStore,
    DashboardViewStore,
    DeleteLeadStore,
    FilterSingleLead,
    Lead,
    LeadAccountStore,
    LeadActivityEmailStore,
    LeadListStore,
    NotesListDialogStore,
    TaskListDialogStore,
    TwilioStore,
} from '../../stores/LeadStore';
import { intitializeDevice } from '../../features/Twilio/TwilioBase';
import { ProblemDetails } from '../../types/ApiErrorResponse';
import { useAuth } from '../../hooks/useAuth';
import {
    ActivityDashboardDialogStore,
    ActivityDialogStore,
    ActivityHistoryDialogStore,
    ActivityNotesDialogStore,
    CadenceListDialogStore,
    CompanyDialogStore,
    DailpadPopupStore,
    HistoryDetailsDialogStore,
    IncomingCallPopupStore,
    LeadOverrideStore,
    LeadScreenStore,
    QuickLeadDialogStore,
    SprintDialogStore,
    ViewType,
} from '../../stores/LeadStore';
import ActivityDialog from '../../features/leads/view/ActivityDialog';
import DoActivityAccountLeadDialog from '../../features/leads/view/DoActivityAccountLeadDialog';
import DoActivitySecondaryDialog from '../../features/leads/view/DoActivitySecondaryDialog';
import DialPadDialog from '../../features/leads/view/DialPadDialog';
import EditLeadView from '../../features/leads/view/EditLeadView';
import IncomingCallDialog from '../../features/Twilio/IncomingCallDialog';
import LeadsScreen from '../../pages/LeadsScreen';
import AlertDialog from '../../features/common/ConfirmationDialog';
import _ from 'lodash';
import HomeIcon from '@mui/icons-material/Home';
import EditDialog from '../../features/leads/view/EditDialog';

interface Props {
    children: ReactNode;
}

const UserLayout = ({ children }: Props) => {
    // ** Hooks
    const { settings, saveSettings } = useSettings();
    const [menus, setMenus] = useState([]);
    const open = ApplicationStore.useState((s) => s.snackbarOpen);
    const message = ApplicationStore.useState((s) => s.snackbarMessage);
    const duration = ApplicationStore.useState((s) => s.snackbarDuration);
    const { user } = useAuth();

    const handleClose = () => {
        ApplicationStore.update((s) => {
            s.snackbarOpen = false;
        });
    };

    useEffect(() => {
        if (user !== null) {
            loadTwillio();
        }
    }, [user]);

    useEffect(() => {
        const get = async () => {
            await client.get(menuEndpoints.getMenus).then((response) => {
                const menus = response.data;
                processMenus(menus);
                setMenus(menus);
            });
        };

        get();
    }, []);

    const loadTwillio = async () => {
        await client
            .get(leadEndpoints.gettwiliotoken)
            .then(async (response) => {
                TwilioStore.update((s) => {
                    s.record = response.data;
                });

                intitializeDevice(response.data.token, response.data.identity, 'trace');
            })
            .catch((err: ProblemDetails) => {
                if (err.errors) {
                    for (const [key, value] of Object.entries(err.errors)) {
                        const errorMessage = '';

                        value.forEach((message) => {
                            message += message + '\\';
                        });
                    }
                }
            });
    };

    const processMenus = (menus: any) => {
        for (let i = 0; i < menus.length; i++) {
            const menu = menus[i];

            if (menu.children) {
                processMenus(menu.children);
            }

            if (menu.children.length === 0) {
                delete menu.children;
            }
            //error_outline

            // menu.icon = () => <Icon>{menu.icon}</Icon>
        }
    };

    /**
     *  The below variable will hide the current layout menu at given screen size.
     *  The menu will be accessible from the Hamburger icon only (Vertical Overlay Menu).
     *  You can change the screen size from which you want to hide the current layout menu.
     *  Please refer useMediaQuery() hook: https://mui.com/material-ui/react-use-media-query/,
     *  to know more about what values can be passed to this hook.
     *  ! Do not change this value unless you know what you are doing. It can break the template.
     */
    const hidden = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));

    const accountLeadMode = ActivityDialogStore.useState((s) => s.accountLeadMode);
    const sprintLeadMode = ActivityDialogStore.useState((s) => s.sprintLeadMode);
    const companyMode = CompanyDialogStore.useState((s) => s.companyMode);
    const sprintMode = CompanyDialogStore.useState((s) => s.sprintMode);
    const sprintDialogMode = SprintDialogStore.useState((s) => s.sprintDialogMode);
    const activityHistorDialogMode = ActivityHistoryDialogStore.useState((s) => s.activityHistorDialogMode);
    const miHistorDialogMode = ActivityHistoryDialogStore.useState((s) => s.miHistorDialogMode);
    const historyDetailsDialogStore = HistoryDetailsDialogStore.useState((s) => s.historyDetailsDialogStore);
    const cadenceListDialogMode = CadenceListDialogStore.useState((s) => s.cadenceListDialogMode);
    const activityNotesDialogMode = ActivityNotesDialogStore.useState((s) => s.activityNotesDialogMode);
    const dialpadDialogMode = DailpadPopupStore.useState((s) => s.dialpadDialogMode);
    const quickLeadDialogMode = QuickLeadDialogStore.useState((s) => s.quickLeadDialogMode);
    const notesDiealogMode = ActivityDashboardDialogStore.useState((s) => s.notesDiealogMode);
    const emailBodyDiealogMode = ActivityDashboardDialogStore.useState((s) => s.emailBodyDiealogMode);
    const smsBodyDiealogMode = ActivityDashboardDialogStore.useState((s) => s.smsBodyDiealogMode);
    const socialMediaDiealogMode = ActivityDashboardDialogStore.useState((s) => s.socialMediaDiealogMode);
    const incomingCallDialogMode = IncomingCallPopupStore.useState((s) => s.incomingCallDialogMode);
    const editLeadMode = ActivityDialogStore.useState((s) => s.editLeadMode);
    const changeActivityMode = LeadOverrideStore.useState((s) => s.changeActivityMode);
    const changeCadenceMode = LeadOverrideStore.useState((s) => s.changeCadenceMode);
    const changeSprintMode = LeadOverrideStore.useState((s) => s.changeSprintMode);
    const changeOwnerMode = LeadOverrideStore.useState((s) => s.changeOwnerMode);
    const notesListDialogMode = NotesListDialogStore.useState((s) => s.notesListDialogMode);
    const taskListDialogMode = TaskListDialogStore.useState((s) => s.taskListDialogMode);
    const deleteLead = DeleteLeadStore.useState((s) => s.deleteLead);
    const changeDateMode = LeadOverrideStore.useState((s) => s.changeDateMode);
    const isAddReferralPopup = LeadScreenStore.useState((s) => s.isAddReferralPopup);
    const filterSingleLead = FilterSingleLead.useState((s) => s.filterSingleLead);

    const activityTemplateSelectionMode = ActivityTemplateSelectionStore.useState(
        (s) => s.activityTemplateSelectionMode,
    );

    const onIncomingCallClose = async () => {
        IncomingCallPopupStore.update((s) => {
            s.incomingCallDialogMode = false;
        });
    };

    const selectedView = LeadScreenStore.useState((s) => s.selectedView);
    const selectedLead = LeadScreenStore.useState((s) => s.selectedLead) as Lead;

    const handleActivityConfirm = async () => {
        LeadScreenStore.update((s) => {
            s.selectedLead = null;
        });
    };

    const onAccountLeadClose = async () => {
        ActivityDialogStore.update((s) => {
            s.accountLeadMode = false;
        });
    };

    const onSprintLeadClose = async () => {
        ActivityDialogStore.update((s) => {
            s.sprintLeadMode = false;
        });
        SprintDialogStore.update((s) => {
            s.sprintDialogMode = false;
        });
    };

    const onCompanyLeadClose = async () => {
        CompanyDialogStore.update((s) => {
            s.companyMode = false;
        });
    };

    const onCompanySprintLeadClose = async () => {
        CompanyDialogStore.update((s) => {
            s.sprintMode = false;
        });
    };

    const onActivityHistoryClose = async () => {
        ActivityHistoryDialogStore.update((s) => {
            s.activityHistorDialogMode = false;
        });
        ActivityHistoryDialogStore.update((s) => {
            s.miHistorDialogMode = false;
        });
    };

    const onActivityHistoryDetailsClose = async () => {
        HistoryDetailsDialogStore.update((s) => {
            s.historyDetailsDialogStore = false;
        });
    };

    const onActivityCadenceClose = async () => {
        CadenceListDialogStore.update((s) => {
            s.cadenceListDialogMode = false;
        });
    };

    const onActivityNotesClose = async () => {
        ActivityNotesDialogStore.update((s) => {
            s.activityNotesDialogMode = false;
        });
    };

    const onDialPadClose = async () => {
        DailpadPopupStore.update((s) => {
            s.dialpadDialogMode = false;
        });
    };

    const onQuickLeadClose = async () => {
        QuickLeadDialogStore.update((s) => {
            s.quickLeadDialogMode = false;
        });
    };

    const onNotesDiealogModeClose = async () => {
        ActivityDashboardDialogStore.update((s) => {
            s.notesDiealogMode = false;
        });
    };

    const onEmailBodyDiealogModeClose = async () => {
        ActivityDashboardDialogStore.update((s) => {
            s.emailBodyDiealogMode = false;
        });
    };

    const onSmsBodyDiealogModeClose = async () => {
        ActivityDashboardDialogStore.update((s) => {
            s.smsBodyDiealogMode = false;
        });
    };

    const onSocialMediaDiealogModeClose = async () => {
        ActivityDashboardDialogStore.update((s) => {
            s.socialMediaDiealogMode = false;
        });
    };

    const onchangeActivityClose = async () => {
        LeadOverrideStore.update((s) => {
            s.changeActivityMode = false;
        });
    };

    const onchangeCadenceClose = async () => {
        LeadOverrideStore.update((s) => {
            s.changeCadenceMode = false;
        });
    };

    const onchangeSprintClose = async () => {
        LeadOverrideStore.update((s) => {
            s.changeSprintMode = false;
        });
    };

    const onchangeOwnerClose = async () => {
        LeadOverrideStore.update((s) => {
            s.changeOwnerMode = false;
        });
    };

    const onchangeDateClose = async () => {
        LeadOverrideStore.update((s) => {
            s.changeDateMode = false;
        });
    };

    const onNotesListDialogModeeClose = async () => {
        NotesListDialogStore.update((s) => {
            s.notesListDialogMode = false;
        });
    };

    const onTaskListDialogModeeClose = async () => {
        TaskListDialogStore.update((s) => {
            s.taskListDialogMode = false;
        });
    };

    const onActivityDialogClose = async () => {
        LeadScreenStore.update((s) => {
            s.selectedView = ViewType.None;
        });
        LeadActivityEmailStore.update((s) => {
            s.record = {
                leadId: '',
                emailBody: '',
                emailTo: '',
                emailFrom: '',
                emailSubject: '',
                userCustomerDetailsId: '',
            };
        });
    };

    const onDeleteLeadClose = async () => {
        DeleteLeadStore.update((s) => {
            s.deleteLead = false;
        });
    };

    const onReferralClose = async () => {
        LeadScreenStore.update((s) => {
            s.isAddReferralPopup = false;
        });
    };

    const onActivityTemplateSelectionClose = async () => {
        ActivityTemplateSelectionStore.update((s) => {
            s.activityTemplateSelectionMode = false;
        });
    };

    const onDeleteLead = async () => {
        await client
            .post(leadEndpoints.deletelead.replace('{leadid}', selectedLead?.id))
            .then(async (response) => {
                //TODO: Reload from DB
                if (filterSingleLead) {
                    DashboardViewStore.update((s) => {
                        s.activityDate = '';
                    });
                    DashboardViewStore.update((s) => {
                        s.showTodoList = true;
                    });
                    DashboardViewStore.update((s) => {
                        s.showActivityWorkList = false;
                    });
                    DashboardViewStore.update((s) => {
                        s.showInboundCallList = false;
                    });
                    DashboardViewStore.update((s) => {
                        s.showDefault = true;
                    });
                    DashboardViewStore.update((s) => {
                        s.showDetail = false;
                    });
                    DashboardViewStore.update((s) => {
                        s.showTaskList = false;
                    });
                    FilterSingleLead.update((s) => {
                        s.filterSingleLead = false;
                    });
                } else {
                    LeadListStore.update((s) => {
                        const list = _.cloneDeep(s.list);
                        const leadIndex = list.items.findIndex((s) => s.id == selectedLead?.id);
                        list.items.splice(leadIndex, 1);
                        s.list = list;
                    });
                }

                DeleteLeadStore.update((s) => {
                    s.deleteLead = false;
                });
            })
            .catch((err: ProblemDetails) => {
                if (err.errors) {
                    for (const [key, value] of Object.entries(err.errors)) {
                        const errorMessage = '';
                        DeleteLeadStore.update((s) => {
                            s.deleteLead = false;
                        });
                        value.forEach((message) => {
                            message += message + '\\';
                        });
                    }
                }
            });
    };

    return (
        <Layout
            hidden={hidden}
            settings={settings}
            saveSettings={saveSettings}
            {...(settings.layout === 'horizontal'
                ? {
                      // ** Navigation Items
                      horizontalNavItems: HorizontalNavItems(),

                      // Uncomment the below line when using server-side menu in horizontal layout and comment the above line
                      // horizontalNavItems: ServerSideHorizontalNavItems(),

                      // ** AppBar Content
                      horizontalAppBarContent: () => (
                          <HorizontalAppBarContent settings={settings} saveSettings={saveSettings} />
                      ),
                  }
                : {
                      // ** Navigation Items
                      verticalNavItems: menus,

                      // Uncomment the below line when using server-side menu in vertical layout and comment the above line
                      // verticalNavItems: ServerSideVerticalNavItems(),

                      // ** AppBar Content
                      verticalAppBarContent: (props) => (
                          <VerticalAppBarContent
                              hidden={hidden}
                              settings={settings}
                              saveSettings={saveSettings}
                              toggleNavVisibility={props.toggleNavVisibility}
                          />
                      ),
                  })}
        >
            {children}
            <div>
                {incomingCallDialogMode && (
                    <IncomingCallDialog
                        maxWidth="md"
                        showIconButton={false}
                        open={incomingCallDialogMode}
                        onCancel={onIncomingCallClose}
                        onConfirm={() => onIncomingCallClose()}
                        title="Incoming Call"
                    />
                )}
            </div>
            {selectedView == ViewType.DoActivity && (
                <ActivityDialog
                    open={selectedLead != null}
                    onCancel={onActivityDialogClose}
                    onConfirm={() => handleActivityConfirm()}
                    lead={selectedLead}
                    key={selectedLead.id}
                />
            )}
            {accountLeadMode && (
                <DoActivityAccountLeadDialog
                    maxWidth="md"
                    showIconButton={true}
                    open={accountLeadMode}
                    onCancel={onAccountLeadClose}
                    onConfirm={() => onAccountLeadClose()}
                    title="Account Leads"
                />
            )}
            {sprintLeadMode && (
                <DoActivityAccountLeadDialog
                    maxWidth="md"
                    showIconButton={true}
                    open={sprintLeadMode}
                    onCancel={onSprintLeadClose}
                    onConfirm={() => onSprintLeadClose()}
                    title="Account & Sprint Leads"
                />
            )}
            {companyMode && (
                <DoActivityAccountLeadDialog
                    maxWidth="md"
                    showIconButton={true}
                    open={companyMode}
                    onCancel={onCompanyLeadClose}
                    onConfirm={() => onCompanyLeadClose()}
                    title="Company Leads"
                />
            )}
            {sprintMode && (
                <DoActivityAccountLeadDialog
                    maxWidth="md"
                    showIconButton={true}
                    open={sprintMode}
                    onCancel={onCompanySprintLeadClose}
                    onConfirm={() => onCompanySprintLeadClose()}
                    title="Company & Sprint Leads"
                />
            )}
            {activityHistorDialogMode && (
                <DoActivityAccountLeadDialog
                    maxWidth="md"
                    showIconButton={true}
                    open={activityHistorDialogMode}
                    onCancel={onActivityHistoryClose}
                    onConfirm={() => onActivityHistoryClose()}
                    title="Activity History"
                />
            )}
            {miHistorDialogMode && (
                <DoActivityAccountLeadDialog
                    maxWidth="md"
                    showIconButton={true}
                    open={miHistorDialogMode}
                    onCancel={onActivityHistoryClose}
                    onConfirm={() => onActivityHistoryClose()}
                    title="Meaningful Interaction History"
                />
            )}
            {historyDetailsDialogStore && (
                <DoActivitySecondaryDialog
                    open={historyDetailsDialogStore}
                    onCancel={onActivityHistoryDetailsClose}
                    onConfirm={() => onActivityHistoryDetailsClose()}
                    title="Activity Details"
                />
            )}
            {cadenceListDialogMode && (
                <DoActivityAccountLeadDialog
                    maxWidth="md"
                    showIconButton={true}
                    open={cadenceListDialogMode}
                    onCancel={onActivityCadenceClose}
                    onConfirm={() => onActivityCadenceClose()}
                    title="Cadence Steps"
                />
            )}
            {activityNotesDialogMode && (
                <DoActivityAccountLeadDialog
                    maxWidth="md"
                    showIconButton={false}
                    open={activityNotesDialogMode}
                    onCancel={onActivityNotesClose}
                    onConfirm={() => onActivityNotesClose()}
                    title="Activity Notes"
                />
            )}
            {dialpadDialogMode && (
                <DialPadDialog
                    maxWidth="xs"
                    showIconButton={true}
                    open={dialpadDialogMode}
                    onCancel={onDialPadClose}
                    onConfirm={() => onActivityNotesClose()}
                    title="DialPad"
                />
            )}
            {sprintDialogMode && (
                <DoActivityAccountLeadDialog
                    maxWidth="md"
                    showIconButton={true}
                    open={sprintDialogMode}
                    onCancel={onSprintLeadClose}
                    onConfirm={() => onSprintLeadClose()}
                    title="Sprint Leads"
                />
            )}
            {quickLeadDialogMode && (
                <DoActivityAccountLeadDialog
                    maxWidth="md"
                    showIconButton={true}
                    open={quickLeadDialogMode}
                    onCancel={onQuickLeadClose}
                    onConfirm={() => onQuickLeadClose()}
                    title="Lead Details"
                />
            )}
            {notesDiealogMode && (
                <DoActivityAccountLeadDialog
                    maxWidth="md"
                    showIconButton={true}
                    open={notesDiealogMode}
                    onCancel={onNotesDiealogModeClose}
                    onConfirm={() => onNotesDiealogModeClose()}
                    title="Activity Notes"
                />
            )}
            {emailBodyDiealogMode && (
                <DoActivityAccountLeadDialog
                    maxWidth="md"
                    showIconButton={true}
                    open={emailBodyDiealogMode}
                    onCancel={onEmailBodyDiealogModeClose}
                    onConfirm={() => onEmailBodyDiealogModeClose()}
                    title="Email Body"
                />
            )}
            {smsBodyDiealogMode && (
                <DoActivityAccountLeadDialog
                    maxWidth="md"
                    showIconButton={true}
                    open={smsBodyDiealogMode}
                    onCancel={onSmsBodyDiealogModeClose}
                    onConfirm={() => onSmsBodyDiealogModeClose()}
                    title="SMS Message"
                />
            )}
            {socialMediaDiealogMode && (
                <DoActivityAccountLeadDialog
                    maxWidth="md"
                    showIconButton={true}
                    open={socialMediaDiealogMode}
                    onCancel={onSocialMediaDiealogModeClose}
                    onConfirm={() => onSocialMediaDiealogModeClose()}
                    title="Social Media Message"
                />
            )}
            {selectedLead && selectedView == ViewType.Edit && editLeadMode && <EditLeadView />}
            {selectedLead && selectedView == ViewType.DoActivity && editLeadMode && <EditLeadView />}
            {changeActivityMode && (
                <DialPadDialog
                    maxWidth="md"
                    showIconButton={true}
                    open={changeActivityMode}
                    onCancel={onchangeActivityClose}
                    onConfirm={() => onchangeActivityClose()}
                    title="Change Activity"
                />
            )}
            {changeCadenceMode && (
                <DialPadDialog
                    maxWidth="md"
                    showIconButton={true}
                    open={changeCadenceMode}
                    onCancel={onchangeCadenceClose}
                    onConfirm={() => onchangeCadenceClose()}
                    title="Change Cadence"
                />
            )}
            {changeSprintMode && (
                <DialPadDialog
                    maxWidth="md"
                    showIconButton={true}
                    open={changeSprintMode}
                    onCancel={onchangeSprintClose}
                    onConfirm={() => onchangeSprintClose()}
                    title="Change Sprint"
                />
            )}
            {changeOwnerMode && (
                <DialPadDialog
                    maxWidth="md"
                    showIconButton={true}
                    open={changeOwnerMode}
                    onCancel={onchangeOwnerClose}
                    onConfirm={() => onchangeOwnerClose()}
                    title="Change Owner"
                />
            )}
            {notesListDialogMode && (
                <DoActivityAccountLeadDialog
                    maxWidth="md"
                    showIconButton={true}
                    open={notesListDialogMode}
                    onCancel={onNotesListDialogModeeClose}
                    onConfirm={() => onNotesListDialogModeeClose()}
                    title="Lead Notes"
                />
            )}
            {taskListDialogMode && (
                <DoActivityAccountLeadDialog
                    maxWidth="md"
                    showIconButton={true}
                    open={taskListDialogMode}
                    onCancel={onTaskListDialogModeeClose}
                    onConfirm={() => onTaskListDialogModeeClose()}
                    title="Lead Tasks"
                />
            )}
            {selectedLead && deleteLead && (
                <AlertDialog
                    open={deleteLead}
                    title="Delete Lead"
                    message="Are you sure you want to delete this lead?"
                    onConfirm={onDeleteLead}
                    onCancel={onDeleteLeadClose}
                    cancelButtonText="Cancel"
                    confirmButtonText="OK"
                />
            )}
            {changeDateMode && (
                <DialPadDialog
                    maxWidth="md"
                    showIconButton={true}
                    open={changeDateMode}
                    onCancel={onchangeDateClose}
                    onConfirm={() => onchangeDateClose()}
                    title="Change Activity Date"
                />
            )}
            {isAddReferralPopup && (
                <EditDialog
                    open={selectedLead != null}
                    onCancel={onReferralClose}
                    onConfirm={onReferralClose}
                    title="Add Referral Lead"
                />
            )}
            {activityTemplateSelectionMode && (
                <DoActivityAccountLeadDialog
                    maxWidth="md"
                    showIconButton={false}
                    open={activityTemplateSelectionMode}
                    onCancel={onActivityTemplateSelectionClose}
                    onConfirm={() => onActivityTemplateSelectionClose()}
                    title="Select Template"
                />
            )}
            <Snackbar open={open} autoHideDuration={duration} onClose={handleClose} message={message} />
        </Layout>
    );
};

export default UserLayout;
